import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import LoadingSpinner from './LoadingSpinner';
import { getCurrentTenant, setCurrentTenant } from '../lib/tenant';
import type { Tenant } from '../types/tenant';

interface TenantContextType {
  tenant: Tenant | null;
  loading: boolean;
  error: string | null;
  refreshTenant: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const refreshTenant = async () => {
    try {
      if (!user) {
        setTenant(null);
        return;
      }

      const currentTenant = await getCurrentTenant();
      setCurrentTenant(currentTenant);
      setTenant(currentTenant);
      
      // Redirect to subscription page if tenant is inactive
      if (currentTenant && currentTenant.status !== 'active') {
        navigate('/subscription');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tenant');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) {
      refreshTenant();
    }
  }, [user, authLoading]);

  return (
    <TenantContext.Provider value={{ tenant, loading, error, refreshTenant }}>
      {loading || authLoading ? (
        <LoadingSpinner fullScreen />
      ) : (
        children
      )}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}